import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

export type LogTone = "info" | "success" | "error" | "warning" | "muted";

export interface LogEntry {
  text: string;
  tone: LogTone;
}

const KEYWORDS = /\b(OK|KO|ERRORE|ERRORI?|FAILED?|SUCCESSO|SUCCESS|WARNING|WARN|INFO|COMPLETATA|COMPLETED)\b/gi;

function toneToColor(tone: LogTone) {
  switch (tone) {
    case "success":
      return "text-emerald-300";
    case "error":
      return "text-rose-300";
    case "warning":
      return "text-amber-300";
    case "muted":
      return "text-slate-500";
    default:
      return "text-slate-100";
  }
}

function highlight(message: string) {
  KEYWORDS.lastIndex = 0;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = KEYWORDS.exec(message)) !== null) {
    if (match.index > lastIndex) {
      parts.push(message.slice(lastIndex, match.index));
    }
    const keyword = match[0];
    parts.push(
      <span key={`${keyword}-${match.index}`} className="text-brand-200 font-semibold">
        {keyword}
      </span>,
    );
    lastIndex = KEYWORDS.lastIndex;
  }
  if (lastIndex < message.length) {
    parts.push(message.slice(lastIndex));
  }
  return parts;
}

const LogLine = React.memo(({ entry, index }: { entry: LogEntry; index: number }) => (
  <div
    className={`flex items-start gap-3 font-mono text-[11px] leading-5 transition-colors ${toneToColor(entry.tone)}`}
  >
    <span className="opacity-25 text-[9px] pt-1 inline-block w-8 text-right select-none font-sans shrink-0">
      {(index + 1).toString().padStart(2, "0")}
    </span>
    <div className="flex-1 whitespace-pre-wrap break-words log-line min-h-[20px]">{highlight(entry.text)}</div>
  </div>
));

LogLine.displayName = "LogLine";

interface Props {
  entries: LogEntry[];
  title?: string;
  isStreaming?: boolean;
  progressLine?: string | null;
}

export function LogViewer({ entries, title = "Log Console", isStreaming = false, progressLine }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [sticky, setSticky] = useState(true);
  const [visibleEntries, setVisibleEntries] = useState<LogEntry[]>(entries);
  const [pending, setPending] = useState<LogEntry[]>([]);
  const seenRef = useRef(entries.length);

  // Use useLayoutEffect to scroll before the browser paints
  useLayoutEffect(() => {
    if (sticky && ref.current) {
      ref.current.scrollTop = ref.current.scrollHeight;
    }
  }, [visibleEntries, sticky, progressLine]);

  const handleScroll = () => {
    if (!ref.current) return;
    const { scrollTop, scrollHeight, clientHeight } = ref.current;
    // Greater tolerance for stickiness
    const atBottom = scrollHeight - (scrollTop + clientHeight) < 50;
    setSticky(atBottom);
  };

  useEffect(() => {
    const prevSeen = seenRef.current;
    if (sticky) {
      setVisibleEntries(entries);
      setPending([]);
      seenRef.current = entries.length;
      return;
    }
    if (entries.length > prevSeen) {
      const newOnes = entries.slice(prevSeen);
      setPending((prev) => [...prev, ...newOnes]);
      seenRef.current = entries.length;
    }
  }, [entries, sticky]);

  const revealPending = () => {
    setVisibleEntries(entries);
    setPending([]);
    seenRef.current = entries.length;
    setSticky(true);
  };

  const rendered = useMemo(
    () =>
      visibleEntries.map((entry, idx) => (
        <LogLine key={idx} entry={entry} index={idx} />
      )),
    [visibleEntries],
  );

  return (
    <div className="relative bg-[#050505] rounded-2xl overflow-hidden border border-white/5 shadow-none focus:outline-none focus:ring-0 focus:ring-offset-0 flex flex-col group/terminal min-h-[320px] max-h-[520px] min-w-0">
      <div className="h-12 bg-white/[0.02] border-b border-white/5 flex items-center px-6 relative">
        <div className="flex gap-1.5 border-r border-white/5 pr-4 mr-4">
          <div className="w-3 h-3 rounded-full bg-red-500/30 border border-red-500/20" />
          <div className="w-3 h-3 rounded-full bg-amber-500/30 border border-amber-500/20" />
          <div className="w-3 h-3 rounded-full bg-emerald-500/30 border border-emerald-500/20" />
        </div>
        <div className="flex items-center gap-2 text-[11px] text-zinc-300 font-mono uppercase tracking-[0.25em]">
          {title}
        </div>
        {isStreaming && (
          <div className="ml-auto flex items-center gap-2 text-emerald-400 text-[10px] font-semibold">
            <span className="flex h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Streaming
          </div>
        )}
      </div>

      <div
        ref={ref}
        onScroll={handleScroll}
        className="flex-1 px-5 py-4 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-900 selection:bg-brand-500/20 selection:text-brand-100 log-scroll scrollbar-gutter-stable"
      >
        <div className="space-y-0.5">
          {rendered}
        </div>
        {progressLine && (
          <div className="mt-1 text-[11px] font-mono text-brand-200 whitespace-pre log-line pl-11 leading-5 min-h-[20px]">
            {progressLine}
          </div>
        )}
        {isStreaming && (
          <div className="flex items-center gap-2 text-amber-400 mt-3 pl-10 text-[11px] font-bold">
            <div className="w-2.5 h-4 bg-amber-500/80 animate-[pulse_0.9s_infinite]" />
            <span>In ascolto dei log...</span>
          </div>
        )}
      </div>

      {!sticky && (
        <button
          type="button"
          className="absolute right-3 -bottom-3 translate-y-full text-xs bg-brand-500 text-white px-3 py-1 rounded-full"
          onClick={revealPending}
        >
          ⬇ {pending.length ? `${pending.length} nuovi log` : "nuovi log"}
        </button>
      )}
    </div>
  );
}
